import type {
  ContestEntryTagSourceApi,
  ContestEntryTagTypeApi,
} from 'api-typings/contests/contests';
import { EntryStatusApi } from 'api-typings/contests/contests';
import type { ContestEntry } from '../contest-entries/contest-entries.model';

/**
 * Contest entries model used in the contest view page.
 */
export interface ContestViewEntry extends ContestEntry {
  readonly commentCount: number;
  readonly sellPrice?: number;
  readonly isLiked: boolean;
  readonly seoUrl: string;
  readonly hasFeedback: ContestEntryHasFeedbackStatus;
  readonly likeCount: number;
  readonly freelancerRating: number;
  /**
   * Tags from the entry's automated requirements evaluation. Only populated
   * when the fetch requests `tag_details`; dismiss/undismiss update actions
   * modify the dismissal state of these tags.
   */
  readonly tags?: readonly ContestViewEntryTag[];
  /**
   * The entry's automated requirements evaluation. Only populated when the
   * fetch requests `entry_evaluation_details` and the entry has been
   * evaluated.
   */
  readonly evaluation?: ContestViewEntryEvaluation;
  /**
   * AI star rating from the entry's evaluation. Only populated when the fetch
   * requests `entry_evaluation_details`.
   */
  readonly aiStarRating?: number;
}

/**
 * A tag on a contest entry, produced by the entry's automated requirements
 * evaluation. `tagType` and `isDismissed` are the only fields read by the
 * dismiss/undismiss update actions; the rest are only present when the tag
 * was populated by the entries fetch.
 */
export interface ContestViewEntryTag {
  readonly tagType: ContestEntryTagTypeApi;
  readonly isDismissed: boolean;
  readonly id?: number;
  readonly tagSource?: ContestEntryTagSourceApi;
  readonly creatorId?: number;
  readonly contestEntryEvaluationId?: number;
  readonly createdAt?: number;
  /** Soft-deleted tags are returned by the fetch so the UI can render the deleted state. */
  readonly isDeleted?: boolean;
  readonly dismissedAt?: number;
  readonly dismissedBy?: number;
}

/**
 * A single structured AI feedback item on a contest entry evaluation.
 * `type` is e.g. `failed_requirement` for an unmet hard brief requirement.
 */
export interface ContestViewEntryEvaluationFeedbackItem {
  readonly type: string;
  readonly title: string;
  readonly detail: string;
}

/**
 * A contest entry's automated (AI) requirements evaluation.
 */
export interface ContestViewEntryEvaluation {
  readonly id: number;
  readonly entryId: number;
  readonly confidence: number;
  readonly aiFeedback: readonly ContestViewEntryEvaluationFeedbackItem[];
  readonly aiStarRatingFeedback?: string;
}

/**
 * Used on server notifications model since entry-related websocket events
 * sends the status as a text.
 */
export enum ContestEntryStatusWebsocketEvent {
  WON = 'Won',
  BOUGHT = 'Bought',
  ELIMINATED = 'Eliminated',
  ACTIVE = 'Active',
  WITHDRAWN = 'Withdrawn',
}

export const CONTEST_ENTRY_TEXT_FORMATS: readonly string[] = ['pdf'];

/** Only includes awarded entries */
export const AWARDED_ENTRY_STATUSES: readonly EntryStatusApi[] = [
  EntryStatusApi.WON,
  EntryStatusApi.WON_CLOSED,
  EntryStatusApi.WON_RUNNER_UP,
  EntryStatusApi.WON_BRONZE,
  EntryStatusApi.WON_SILVER,
  EntryStatusApi.WON_GOLD,
  EntryStatusApi.CHOSEN,
];

/** Includes both awarded and bought entries */
export const AWARDED_BOUGHT_ENTRY_STATUSES: readonly EntryStatusApi[] = [
  EntryStatusApi.WON,
  EntryStatusApi.WON_CLOSED,
  EntryStatusApi.WON_RUNNER_UP,
  EntryStatusApi.WON_BRONZE,
  EntryStatusApi.WON_SILVER,
  EntryStatusApi.WON_GOLD,
  EntryStatusApi.CHOSEN,
  EntryStatusApi.BOUGHT,
];

export enum ContestEntryFileType {
  IMAGE,
  VIDEO,
  TEXT,
}

export enum ContestEntryHasFeedbackStatus {
  YES = 'yes',
  NO = 'no',
  UNKNOWN = 'unknown',
}
